/**
 * What a menu *is*, with no DOM in it — so a `model.ts` may build one under R13 and `Menu.tsx` only
 * renders it.
 *
 * These three types were `screens/watch/model.ts`'s. `ui/index.ts` said the player's
 * menus would stay in the player until a second caller appeared, "because one caller is not a
 * pattern", and 2c's filter row is that caller: four menus over the same shape, one of them
 * multi-select. So the *shape* lifted here, the player's ids and copy stayed there, and the
 * behavioural differences between the two callers became props rather than two components.
 */

export interface MenuItemModel {
  readonly id: string;
  readonly label: string;
  /** A second line: a bitrate, "auto-generated", "under 4 minutes", the reason an option is refused. */
  readonly detail: string | null;
  readonly selected: boolean;
  /** An option that cannot be chosen right now, with `detail` saying why (the menu-sections row). */
  readonly disabled?: boolean;
  /**
   * An item mid-async-attempt: disabled, with a spinner in place of its tick (the Watch on menu's
   * PeerTube row while its search is in flight). Implies `disabled` — a caller need not set both.
   */
  readonly busy?: boolean;
  /**
   * An independent on/off row inside a single-select menu — the player's stable-volume switch,
   * which sits under a list of languages that are one-of-these. Announced as a checkbox where its
   * siblings are radios, because "any of these" versus "one of these" is exactly what a reader has
   * to know before pressing it. Redundant under `multiSelect`, where every item is one already.
   */
  readonly checkbox?: boolean;
  /**
   * An item that *does* something rather than *marks a choice* — copy a link, run a command. Plain
   * `menuitem`, no tick and no checked state, because announcing "not checked" about an action is
   * a claim the row never made. `selected` is ignored.
   */
  readonly action?: boolean;
  /**
   * An action item that is a **link out of the app**: rendered as a real `<a>` (so middle-click,
   * "copy link address" and a screen reader's link list keep working) with `rel="noreferrer"` —
   * these are foreign origins, and the referrer is nobody's business (the jump-out pills'
   * reasoning, kept through the merge into a menu). It navigates the current tab (2026-08-02):
   * following a jump-out means "keep watching over there". Implies {@link action}.
   */
  readonly href?: string;
  /**
   * A {@link href} row that opens a new tab instead of taking this one. What /embed's Watch on menu
   * asks for (2026-09-14): the page sits in somebody else's `<iframe>`, so navigating this tab would
   * put the destination inside their frame and take the embedder's page with it. `rel="noreferrer"`
   * already stops the new tab from reaching back through `window.opener`.
   */
  readonly newTab?: boolean;
}

export interface MenuSectionModel {
  readonly title: string | null;
  readonly items: readonly MenuItemModel[];
}

export interface MenuModel {
  /** The control's accessible name. */
  readonly label: string;
  /** The current setting, shown on the trigger so the menu need not be opened to read it. */
  readonly value: string;
  /**
   * What a *tight* trigger shows instead of `value` — a language code where the value is a language
   * name. `undefined` falls back to `value`; `null` shows nothing beside the trigger's icon (a
   * subtitle control that is off has no code to state). The accessible name always carries the full
   * `value` — this only decides what is printed on the bar.
   */
  readonly valueShort?: string | null;
  /**
   * Whether the trigger's value wears the accent — "this filter is set away from its
   * default". The *model* decides, because "default" is a fact about the query, not about a menu.
   */
  readonly valueActive?: boolean;
  readonly sections: readonly MenuSectionModel[];
  /**
   * A caveat about the menu itself — §7's "state it, do not substitute" (the manifest has no 1080p),
   * and 2c's version of the same rule (this filter is unavailable, and why).
   */
  readonly note: string | null;
}

/**
 * A menu of actions under one name — "Watch on", "Share". `value: ''` is the deliberate part: an
 * action menu *has* no current setting, and the empty string tells the trigger to print the label
 * alone rather than a name-colon-nothing.
 */
export function actionMenu(label: string, items: readonly MenuItemModel[]): MenuModel {
  return { label, value: '', valueShort: null, sections: [{ title: null, items }], note: null };
}
